// THE LAPSED CUSTODY SIGN-IN, SAID ONCE (his "why it all goes red everytime", 2 Nov 2026). Cloudflare Access in front of the
// custody API asks again every 24 h (his 26 Oct Face ID rule); until he signs in, every custody read fails the same way
// (lib/api.ts request() → ApiError 'custody_signin'). The custody shells draw ONE line — "Sign in to custody" and the way
// in — and every failed-read line below it goes quiet for that one error (SigninQuiet); any other failure keeps its own
// red line. Nothing is unmounted: a signing sheet open when the sign-in lapses keeps its state. Self-contained markup (no
// kit import): the kits' ReadFailed lines import SigninQuiet from here.
import { createContext, useCallback, useContext, useEffect, useRef, useSyncExternalStore, type ReactNode } from 'react';
import { useQueryClient, type QueryClient } from '@tanstack/react-query';
import { CUSTODY_SIGNIN_URL } from '../../../lib/api';
import { isCustodySignin } from '../../../lib/errors';

/** Inside a custody shell its banner says it; outside (Home's approvals), the failed line itself offers the sign-in. */
const InCustodyShell = createContext(false);

/** Does any custody read stand failed on the lapsed sign-in right now? */
export function custodySignedOut(qc: QueryClient): boolean {
  // Only reads ON SCREEN count: a failed read left in the cache by a panel no longer shown is never asked again, and would
  // keep custody hidden after he signed in (Astra, 2 Nov 2026).
  return qc.getQueryCache().getAll().some((q) => q.queryKey[0] === 'custody' && q.getObserversCount() > 0 && isCustodySignin(q.state.error));
}

function useSignedOut(): boolean {
  const qc = useQueryClient();
  const subscribe = useCallback((on: () => void) => qc.getQueryCache().subscribe(on), [qc]);
  const read = () => custodySignedOut(qc);
  return useSyncExternalStore(subscribe, read, read);
}

/** Opens the sign-in in its own tab; when he comes back to this one, every custody read is asked again. */
function useSignIn(): () => void {
  const qc = useQueryClient();
  const armed = useRef(false);
  useEffect(() => {
    const back = () => {
      if (!armed.current) return;
      armed.current = false;
      void qc.invalidateQueries({ queryKey: ['custody'] });
    };
    window.addEventListener('focus', back);
    return () => window.removeEventListener('focus', back);
  }, [qc]);
  return () => {
    armed.current = true;
    window.open(CUSTODY_SIGNIN_URL, '_blank', 'noopener');
  };
}

function SignInButton() {
  const signIn = useSignIn();
  return (
    <button type="button" className="btn-secondary min-h-[44px] whitespace-nowrap px-4 text-body lg:min-h-[40px]" onClick={signIn}>
      Sign in
    </button>
  );
}

/** The custody shells' one line, above everything: drawn only while a custody read is failing on the sign-in. */
export function CustodySigninShell({ children }: { children: ReactNode }) {
  const out = useSignedOut();
  return (
    <InCustodyShell.Provider value>
      {out && (
        <div role="status" className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-warn/25 bg-warn-bg px-4 py-3 text-body text-warn lg:mb-5">
          <span className="min-w-0">Sign in to custody</span>
          <span className="shrink-0"><SignInButton /></span>
        </div>
      )}
      {/* ONE PROMPT ON THE SCREEN (his word, 2 Nov 2026: "one 'Sign in' prompt on the screen instead of a wall of red"):
          while signed out the custody body is hidden, not unmounted — a signing sheet open when the sign-in lapsed keeps
          its state and shows again once he is back. */}
      <div className={out ? 'hidden' : undefined}>{children}</div>
    </InCustodyShell.Provider>
  );
}

/** What a failed custody read shows when the failure is the lapsed sign-in: inside a custody shell nothing (its banner says
 *  it once); elsewhere one quiet line with the way in. Returns null when the error is something else — the caller then
 *  draws its own failure. */
export function SigninQuiet({ error, className = '' }: { error: unknown; className?: string }) {
  const inShell = useContext(InCustodyShell);
  if (!isCustodySignin(error)) return null;
  if (inShell) return <p className={`text-meta text-tg-hint ${className}`}>Waiting for sign-in.</p>;
  return (
    <p className={`flex flex-wrap items-center gap-x-3 gap-y-1 text-body text-warn ${className}`}>
      <span className="min-w-0">Sign in to custody</span>
      <SignInButton />
    </p>
  );
}
